export { degToRad, radToDeg } from '@learnforge/demo-math/geometry';

const SPECIAL_RADIAN_FRACTIONS: Array<{ num: number; den: number }> = [
  { num: 0, den: 1 },
  { num: 1, den: 6 },
  { num: 1, den: 4 },
  { num: 1, den: 3 },
  { num: 1, den: 2 },
  { num: 2, den: 3 },
  { num: 3, den: 4 },
  { num: 5, den: 6 },
  { num: 1, den: 1 },
  { num: 7, den: 6 },
  { num: 5, den: 4 },
  { num: 4, den: 3 },
  { num: 3, den: 2 },
  { num: 5, den: 3 },
  { num: 7, den: 4 },
  { num: 11, den: 6 },
  { num: 2, den: 1 },
];

const SPECIAL_DEGREES = [
  0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330, 360,
];

export function snapToSpecialRadian(
  r: number,
  epsilon = 0.01,
): { num: number; den: number } | null {
  let best: { num: number; den: number } | null = null;
  let bestDelta = Infinity;
  for (const frac of SPECIAL_RADIAN_FRACTIONS) {
    const target = (frac.num * Math.PI) / frac.den;
    const delta = Math.abs(r - target);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = frac;
    }
  }
  return bestDelta <= epsilon ? best : null;
}

export function snapToSpecialDegree(d: number, epsilon = 1.5): number | null {
  let best: number | null = null;
  let bestDelta = Infinity;
  for (const deg of SPECIAL_DEGREES) {
    const delta = Math.abs(d - deg);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = deg;
    }
  }
  return bestDelta <= epsilon ? best : null;
}

/**
 * Symbolic π-fraction when `r` is within ε of one (`'π/4'`, `'−3π/2'`, `'2π'`,
 * `'0'`), otherwise `sign + fallback(Math.abs(r))` — by default three decimals
 * with trailing zeros trimmed. `fallback` receives the absolute value; the
 * U+2212 sign is applied uniformly by this function.
 */
export function formatRadian(
  r: number,
  fallback: (v: number) => string = (v) => String(Number(v.toFixed(3))),
): string {
  const sign = r < 0 ? '−' : '';
  const snap = snapToSpecialRadian(Math.abs(r));
  if (snap) {
    if (snap.num === 0) return '0';
    if (snap.num === snap.den) return `${sign}π`;
    if (snap.den === 1) return `${sign}${snap.num}π`;
    if (snap.num === 1) return `${sign}π/${snap.den}`;
    return `${sign}${snap.num}π/${snap.den}`;
  }
  return sign + fallback(Math.abs(r));
}
